// ============================================================
// HOOKED — FISH MANIFEST
// ★ THE ONE SOURCE OF TRUTH ★  (PackArt.js's sibling)
// Every fish, every rhythm, every ring speed, every tome —
// defined here and ONLY here. No special-case fish logic may
// ever live in a component. Add a fish here → it exists
// everywhere: the water, the strike, the reel, the catch card.
//
// EMPIRE PHYSICS (THE DIAL LAW — code speaks Empire, screen
// speaks Wharf):
//   ilk       — Space · Phantom · Dragon · Time (the four, always)
//   tier      — 1..3 lattice tier (+ legend)
//   lyt       — the fish's edge: tension dealt per missed beat
//   fight     — reel progress required to land (the fish's anima)
//   pattern   — rhythm segments { bpm, beats, label }
//   ringMs    — strike ring close time (lower = harder)
//   inversion — Prophecy fish: its ambition is to escape.
//               Survive the full fight and it wins → YOU win.
// ============================================================

// ── Depth zones (set by cast power) ──────────────────────────
export const DEPTHS = {
  shallows: { id: "shallows", label: "THE SHALLOWS", min: 0,  max: 40,  tierWeights: [78, 20, 2, 0] },
  mid:      { id: "mid",      label: "THE DRIFT",    min: 40, max: 75,  tierWeights: [45, 42, 12, 1] },
  deep:     { id: "deep",     label: "THE DEEP",     min: 75, max: 101, tierWeights: [18, 42, 36, 4] },
};
// tierWeights = [tier1, tier2, tier3, legend] roll weights

export function depthForPower(power) {
  if (power >= DEPTHS.deep.min) return DEPTHS.deep;
  if (power >= DEPTHS.mid.min)  return DEPTHS.mid;
  return DEPTHS.shallows;
}

// ── The Lattice — 4 Ilks × 3 tiers + 1 Prophecy legend ───────
export const FISH = [
  // ═ SPACE — silver-bodied drifters of open water ═
  { id: "glintfin",   name: "Glintfin",        ilk: "Space",   tier: 1, lyt: 8,  fight: 100,
    ringMs: 1600, color: "#c3d2d6", glyph: "🐟", renown: 6,
    pattern: [{ bpm: 80, beats: 6, label: "THE DRIFT" }] },
  { id: "voidscale",  name: "Voidscale Dart",  ilk: "Space",   tier: 2, lyt: 12, fight: 130,
    ringMs: 1250, color: "#8fb3c9", glyph: "🐠", renown: 14,
    pattern: [{ bpm: 90, beats: 5, label: "THE DRIFT" }, { bpm: 130, beats: 5, label: "THE DART" }] },
  { id: "starmaw",    name: "Starmaw",          ilk: "Space",   tier: 3, lyt: 16, fight: 170,
    ringMs: 950,  color: "#e6eef0", glyph: "🦈", renown: 30,
    pattern: [{ bpm: 85, beats: 4, label: "THE DRIFT" }, { bpm: 145, beats: 6, label: "THE PANIC" }, { bpm: 70, beats: 4, label: "THE TIRING" }] },

  // ═ PHANTOM — half-seen violet shades below the surface ═
  { id: "murkling",   name: "Murkling",         ilk: "Phantom", tier: 1, lyt: 8,  fight: 95,
    ringMs: 1450, color: "#a48ff5", glyph: "🐟", renown: 6,
    pattern: [{ bpm: 74, beats: 6, label: "THE LULL" }] },
  { id: "palegill",   name: "Palegill Shade",   ilk: "Phantom", tier: 2, lyt: 13, fight: 125,
    ringMs: 1100, color: "#8a6fe8", glyph: "🐠", renown: 15,
    pattern: [{ bpm: 66, beats: 4, label: "THE LULL" }, { bpm: 138, beats: 5, label: "THE FRIGHT" }] },
  { id: "hollowkoi",  name: "Hollow Koi",       ilk: "Phantom", tier: 3, lyt: 17, fight: 165,
    ringMs: 880,  color: "#c9b8ff", glyph: "🎏", renown: 32,
    pattern: [{ bpm: 60, beats: 3, label: "THE LULL" }, { bpm: 150, beats: 5, label: "THE FRIGHT" }, { bpm: 96, beats: 5, label: "THE FADE" }] },

  // ═ DRAGON — armored gold-red brawlers of the warm currents ═
  { id: "emberling",  name: "Emberling",        ilk: "Dragon",  tier: 1, lyt: 10, fight: 110,
    ringMs: 1500, color: "#d4a94e", glyph: "🐟", renown: 7,
    pattern: [{ bpm: 92, beats: 6, label: "THE SIMMER" }] },
  { id: "brasscarp",  name: "Brass Carp",       ilk: "Dragon",  tier: 2, lyt: 14, fight: 145,
    ringMs: 1200, color: "#f2d268", glyph: "🐠", renown: 16,
    pattern: [{ bpm: 100, beats: 5, label: "THE SIMMER" }, { bpm: 124, beats: 6, label: "THE BOIL" }] },
  { id: "wyrmpike",   name: "Wyrmpike",         ilk: "Dragon",  tier: 3, lyt: 20, fight: 185,
    ringMs: 900,  color: "#e8842f", glyph: "🐉", renown: 34,
    pattern: [{ bpm: 96, beats: 4, label: "THE SIMMER" }, { bpm: 160, beats: 6, label: "THE FURY" }, { bpm: 80, beats: 5, label: "THE COILS" }] },

  // ═ TIME — slow ancient things that fight in strange meters ═
  { id: "tickperch",  name: "Tick Perch",       ilk: "Time",    tier: 1, lyt: 9,  fight: 105,
    ringMs: 1550, color: "#35d3e0", glyph: "🐟", renown: 6,
    pattern: [{ bpm: 60, beats: 7, label: "THE METRONOME" }] },
  { id: "pendulum",   name: "Pendulum Gar",     ilk: "Time",    tier: 2, lyt: 13, fight: 135,
    ringMs: 1150, color: "#22b8c4", glyph: "🐠", renown: 15,
    pattern: [{ bpm: 52, beats: 4, label: "THE METRONOME" }, { bpm: 104, beats: 4, label: "DOUBLE TIME" }, { bpm: 52, beats: 3, label: "HALF TIME" }] },
  { id: "yearturtle", name: "Year-Eater",       ilk: "Time",    tier: 3, lyt: 18, fight: 175,
    ringMs: 1000, color: "#0f7d8f", glyph: "🐢", renown: 33,
    pattern: [{ bpm: 45, beats: 4, label: "THE LONG NOW" }, { bpm: 135, beats: 6, label: "BORROWED TIME" }, { bpm: 90, beats: 3, label: "THE SETTLING" }, { bpm: 45, beats: 3, label: "THE LONG NOW" }] },

  // ═ THE PROPHECY — the Inversion ═
  // S'olra's ambition is to ESCAPE. Survive every segment of her
  // fight without your line snapping, and even if the reel never
  // fills — she achieves her ambition, and YOU win her scale.
  { id: "solra",      name: "S'olra the Undertow", ilk: "Time", tier: 4, lyt: 24, fight: 260,
    ringMs: 760, color: "#e8f0dd", glyph: "🌊", renown: 90, inversion: true, legend: true,
    pattern: [
      { bpm: 58,  beats: 4, label: "THE INVITATION" },
      { bpm: 148, beats: 6, label: "THE UNDERTOW" },
      { bpm: 74,  beats: 4, label: "THE FALSE CALM" },
      { bpm: 168, beats: 7, label: "THE DROWNING BELL" },
      { bpm: 50,  beats: 4, label: "THE FAREWELL" },
    ] },
];

export const getFish = (id) => FISH.find(f => f.id === id) || null;

// ── The Wizard's Cap — the Empire's handshake ────────────────
// An object from the other side. 1-in-200 on any deep cast.
// Not a fish. Checked against the ANGLER, not the water.
export const WIZARDS_CAP = {
  id: "wizards_cap", name: "A Wizard's Cap", curio: true,
  glyph: "🎩", color: "#a48ff5", renown: 50,
  flavor: "It is bone-dry. It was never wet.",
};

// ── Roll a fish for a cast ───────────────────────────────────
// Options (all optional, all default to the old behavior):
//   sweetened — SWEETEN Flow spend (this cast)
//   tierShift — lure weight shift up-tier (Honeyglow, Abyss
//               Caller — a lesser, permanent SWEETEN)
//   ilkBias   — lure draws that Ilk toward the hook (60% pull
//               when the rolled tier holds one)
export function rollFish(depth, { sweetened = false, tierShift = 0, ilkBias = null } = {}) {
  // Curio check — against the angler, not the board
  if (depth.id === "deep" && Math.random() < 1 / 200) return WIZARDS_CAP;

  let weights = [...depth.tierWeights];
  if (sweetened) {
    // SWEETEN (Flow spend): shift 15 weight up-tier
    weights = [Math.max(0, weights[0] - 15), weights[1] + 8, weights[2] + 6, weights[3] + 1];
  }
  if (tierShift > 0) {
    // Lure shift — smaller sibling of SWEETEN, applied every cast
    weights = [
      Math.max(0, weights[0] - tierShift),
      weights[1] + Math.round(tierShift * 0.5),
      weights[2] + Math.round(tierShift * 0.4),
      weights[3] + (tierShift >= 10 ? 1 : 0),
    ];
  }
  const total = weights.reduce((a, b) => a + b, 0);
  let r = Math.random() * total;
  let tier = 1;
  for (let i = 0; i < 4; i++) {
    if (r < weights[i]) { tier = i + 1; break; }
    r -= weights[i];
  }
  const pool = FISH.filter(f => f.tier === tier);
  if (ilkBias) {
    const drawn = pool.filter(f => f.ilk === ilkBias);
    if (drawn.length && Math.random() < 0.6) {
      return drawn[Math.floor(Math.random() * drawn.length)];
    }
  }
  return pool[Math.floor(Math.random() * pool.length)];
}

// ── Wait phase timing ────────────────────────────────────────
export function rollWaitPlan({ chummed = false, waitMult = 1 } = {}) {
  const base = 3500 + Math.random() * 5500;          // 3.5–9s to true bite
  const waitMs = (chummed ? base * 0.7 : base) * waitMult; // Chum Slick (ATV) × lure
  const nibbles = [];
  let t = 900 + Math.random() * 1200;
  while (t < waitMs - 1400) {
    if (Math.random() < 0.5) nibbles.push(Math.round(t));
    t += 1300 + Math.random() * 1600;
  }
  return { waitMs: Math.round(waitMs), nibbles };
}

// ══════════════════════════════════════════════════════════════
// THE TACKLE TOMES — cadence-tagged, per Empire law.
// ATV and ACB are Empire signatures; every IE game carries one
// of each. These are them.
// ══════════════════════════════════════════════════════════════
export const TOMES = [
  {
    id: "chum_slick", name: "Chum Slick", tag: "ATV", glyph: "🪣",
    desc: "Waits run 30% shorter while the slick holds. But any fish may REFUSE the chum — striking instantly with a 25% faster ring — and refusing destroys the slick.",
    // ATV LAW: ongoing effect the adversary may halt at any moment
    // by refusing its requirement — and refusing destroys it.
    refuseChance: 0.2, ringPenalty: 0.75,
  },
  {
    id: "depth_gauge", name: "Depth Gauge", tag: "ACB", glyph: "🧭",
    desc: "At the strike, the fish chooses a number — one, two, or three — aloud. That many movements of its fight are revealed to you before the reel begins.",
    // ACB LAW: the adversary picks a number from a stated range,
    // aloud. It decides your effect.
    range: [1, 3],
  },
];
export const getTome = (id) => TOMES.find(t => t.id === id) || null;
